// Shared presentational primitives for the console — the component layer of
// the design system in docs/design/DESIGN_SYSTEM.md ("Control room, calm").
//
// Rules encoded here:
//   • surfaces: page surface-0 → panel surface-1 → raised surface-2 → overlay
//     surface-3, hairline borders (line / line-strong), shadows only on overlays.
//   • lime (#CCFF00) is budgeted: ActionButton variant="solid" is the ONE
//     primary action per view; focus rings; the active-nav marker. Never data.
//   • numbers are Outfit with tabular figures (font-mono, the numeric face),
//     right-aligned in tables; JetBrains Mono (font-code) only for hashes,
//     ids, tokens and keyboard keys.
//   • every data-bearing primitive has loading / empty / error affordances.
//   • touch sizing keys on the input type, not the viewport: `coarse:` (see
//     tailwind.config.cjs) lifts every interactive primitive to ≥ 44 px under a
//     coarse pointer at any width; fine pointers keep the 32/36 px density.
//
// Keep this file component-only (react-refresh); hooks and constants live in
// density.ts / useFeedHealth.ts / products.tsx.

import { useCallback, useEffect, useId, useRef, type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import * as RTooltip from "@radix-ui/react-tooltip";
import type { LucideIcon } from "lucide-react";
import { AlertTriangle, ChevronLeft, ChevronRight, Inbox, Loader2, RefreshCw, WifiOff, X } from "lucide-react";
import { AnimatePresence, motion, useDragControls, useReducedMotion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

import { Sparkline } from "@/components/charts";
import { AnimatedNumber, HoverLift } from "@/app/console/motion";
import { useDensity, type Density } from "@/app/console/density";
import { CardTitleContext } from "@/app/console/cardContext";
import { useHScrollFade, useSwipe } from "@/app/console/useHScroll";
import { useIsPhoneWidth } from "@/app/console/useViewport";
import { appForPath } from "@/app/products";

// ---------------------------------------------------------------------------
// Semantic tones shared by Pill / StatusDot / KpiStrip
// ---------------------------------------------------------------------------

export type SemanticTone = "ok" | "warn" | "danger" | "info" | "neutral";

const TONE_HEX: Record<SemanticTone, string> = {
  ok: "var(--pb-ok)",
  warn: "var(--pb-warn)",
  danger: "var(--pb-danger)",
  info: "var(--pb-info)",
  neutral: "var(--pb-text-4)",
};

// ---------------------------------------------------------------------------
// ProductIcon — the product glyph on a soft product tint. Five sizes: xs/sm are
// chrome (header crumb, sidebar); md/lg/xl are the 48/56/72 px launcher tiles.
// ---------------------------------------------------------------------------

export type ProductIconSize = "xs" | "sm" | "md" | "lg" | "xl";

const ICON_PX: Record<ProductIconSize, { box: number; glyph: number; radius: number }> = {
  xs: { box: 28, glyph: 15, radius: 8 },
  sm: { box: 36, glyph: 18, radius: 10 },
  md: { box: 48, glyph: 22, radius: 12 },
  lg: { box: 56, glyph: 26, radius: 14 },
  xl: { box: 72, glyph: 32, radius: 16 },
};

export function ProductIcon({
  icon: Icon,
  accent,
  size = "md",
  className,
  style,
  label,
}: {
  icon: LucideIcon;
  accent: string;
  size?: ProductIconSize;
  className?: string;
  /** Override box/glyph sizing (e.g. fluid clamp() on the login brand panel). */
  style?: CSSProperties;
  /** Accessible name; omit when purely decorative next to a visible label. */
  label?: string;
}) {
  const s = ICON_PX[size];
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center ${className ?? ""}`}
      style={{
        width: s.box,
        height: s.box,
        borderRadius: s.radius,
        backgroundColor: `${accent}1a`,
        color: accent,
        boxShadow: `inset 0 0 0 1px ${accent}2e`,
        ...style,
      }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <Icon style={{ width: s.glyph, height: s.glyph }} strokeWidth={1.75} aria-hidden="true" />
    </span>
  );
}

// ---------------------------------------------------------------------------
// StatusDot — live/stale/offline, ok/watch/warning/critical, in one glyph
// ---------------------------------------------------------------------------

export function StatusDot({
  tone = "neutral",
  size = "md",
  pulse,
  label,
  title,
  className,
}: {
  tone?: SemanticTone;
  size?: "sm" | "md";
  /** Soft pulse for a live feed; off under reduced motion. */
  pulse?: boolean;
  /** Optional text rendered after the dot. */
  label?: ReactNode;
  title?: string;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const px = size === "sm" ? 6 : 8;
  const color = TONE_HEX[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 ${className ?? ""}`} title={title}>
      <span className="relative inline-flex shrink-0" style={{ width: px, height: px }} aria-hidden="true">
        {pulse && !reduce && (
          <span
            className="absolute inset-0 animate-ping rounded-full opacity-60"
            style={{ backgroundColor: color, animationDuration: "2.4s" }}
          />
        )}
        <span className="relative inline-flex h-full w-full rounded-full" style={{ backgroundColor: color }} />
      </span>
      {label !== undefined && <span className="text-[13px] leading-5 text-text-2">{label}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tooltip — styled overlay-surface tooltip (Radix). Use instead of `title` on
// icon-only controls: the header status dot, the 72 px sidebar rail, toolbars.
// The child must accept a ref (button, a, NavLink).
// ---------------------------------------------------------------------------

export function Tooltip({
  content,
  children,
  side = "bottom",
  delay = 150,
}: {
  content: ReactNode;
  children: ReactElement;
  side?: "top" | "bottom" | "left" | "right";
  delay?: number;
}) {
  return (
    <RTooltip.Provider delayDuration={delay} skipDelayDuration={400}>
      <RTooltip.Root>
        <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
        <RTooltip.Portal>
          <RTooltip.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className="z-popover max-w-[260px] rounded-[6px] border border-line-strong bg-surface-3 px-2 py-1 text-[12px] leading-4 text-text-1 shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
          >
            {content}
            <RTooltip.Arrow width={10} height={5} style={{ fill: "var(--pb-surface-3)" }} />
          </RTooltip.Content>
        </RTooltip.Portal>
      </RTooltip.Root>
    </RTooltip.Provider>
  );
}

// ---------------------------------------------------------------------------
// Kbd — keyboard hint chip
// ---------------------------------------------------------------------------

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-[5px] border border-line-strong bg-surface-2 px-1.5 font-code text-[10.5px] font-medium leading-none text-text-3 ${className ?? ""}`}
    >
      {children}
    </kbd>
  );
}

// ---------------------------------------------------------------------------
// Skeleton — loading placeholder bar
// ---------------------------------------------------------------------------

export function Skeleton({ className, width, height = 14 }: { className?: string; width?: number | string; height?: number | string }) {
  return <span className={`pb-skeleton block ${className ?? ""}`} style={{ width: width ?? "100%", height }} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// PageHeader — title, subtitle, product crumb (derived from the route unless
// overridden), actions, optional KPI strip. Every page template starts here.
// ---------------------------------------------------------------------------

export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
  hideProduct,
  kpis,
  kpiColumns,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  /** Replace the derived "Product" crumb (e.g. "Comply · Records"). */
  eyebrow?: ReactNode;
  hideProduct?: boolean;
  /** Optional KPI strip rendered under the title row. */
  kpis?: KpiItem[];
  kpiColumns?: 3 | 4 | 5 | 6;
}) {
  const location = useLocation();
  const app = appForPath(location.pathname);
  const crumb = eyebrow ?? (hideProduct ? null : app.name);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          {crumb && (
            <div className="mb-1.5 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: app.accent }} aria-hidden="true" />
              <span className="pb-micro text-text-3">{crumb}</span>
            </div>
          )}
          <h1 className="text-xl font-semibold leading-8 tracking-tight text-text-1 sm:text-2xl 3xl:text-[1.65rem] 3xl:leading-9">
            {title}
          </h1>
          {subtitle && <p className="mt-1 max-w-2xl text-sm leading-[22px] text-text-2">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {kpis && kpis.length > 0 && <KpiStrip items={kpis} columns={kpiColumns} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card / Section — boxed panel vs. unboxed titled region
// ---------------------------------------------------------------------------

export function Card({
  title,
  description,
  actions,
  children,
  className,
  accent,
  padding = "md",
}: {
  title?: ReactNode;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Product/semantic colour drawn as a 2 px left rail. */
  accent?: string;
  padding?: "none" | "sm" | "md";
}) {
  const pad = padding === "none" ? "" : padding === "sm" ? "p-3 sm:p-4" : "p-4 sm:p-5 4xl:p-6";
  return (
    <CardTitleContext.Provider value={typeof title === "string" ? title : null}>
      <section
        className={`relative min-w-0 overflow-hidden rounded-card border border-line bg-surface-1 ${pad} ${className ?? ""}`}
      >
        {accent && <span className="absolute inset-y-0 left-0 w-0.5" style={{ backgroundColor: accent }} aria-hidden="true" />}
        {(title || actions) && (
          <div className={`flex flex-wrap items-start justify-between gap-3 ${padding === "none" ? "px-4 pt-4 sm:px-5 sm:pt-5" : ""} mb-4`}>
            <div className="min-w-0">
              {title && <h2 className="text-[15px] font-semibold leading-[22px] text-text-1">{title}</h2>}
              {description && <p className="mt-0.5 text-[13px] leading-5 text-text-3">{description}</p>}
            </div>
            {actions}
          </div>
        )}
        {children}
      </section>
    </CardTitleContext.Provider>
  );
}

export function Section({
  title,
  description,
  actions,
  children,
  className,
  accent,
}: {
  title: ReactNode;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  accent?: string;
}) {
  return (
    <section className={`min-w-0 ${className ?? ""}`}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {accent && <span className="h-4 w-0.5 rounded-full" style={{ backgroundColor: accent }} aria-hidden="true" />}
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold leading-[22px] text-text-1">{title}</h2>
            {description && <p className="mt-0.5 text-[13px] leading-5 text-text-3">{description}</p>}
          </div>
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// StatTile — one number, mono + tabular
// ---------------------------------------------------------------------------

export function StatTile({
  label,
  value,
  hint,
  accent = "var(--pb-text-1)",
  unit,
  tone,
  loading,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
  /** Muted unit after the number, e.g. "₹/MWh" or "%". */
  unit?: string;
  /** Status dot next to the label. */
  tone?: SemanticTone;
  loading?: boolean;
}) {
  return (
    <div className="flex h-full flex-col rounded-card border border-line bg-surface-1 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="pb-micro text-text-3">{label}</span>
        {tone && <StatusDot tone={tone} size="sm" />}
      </div>
      {loading ? (
        <Skeleton className="mt-3" width="60%" height={24} />
      ) : (
        <div
          className="mt-2 min-w-0 font-mono text-xl font-medium tabular-nums tracking-[-0.01em] [overflow-wrap:anywhere] sm:text-2xl"
          style={{ color: accent }}
        >
          {value}
          {unit && <span className="ml-1 font-sans text-xs font-normal text-text-3">{unit}</span>}
        </div>
      )}
      {hint && <div className="mt-1 text-xs leading-4 text-text-3">{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KpiStrip — one row of facts (mono numbers, hairline dividers, status dots)
// ---------------------------------------------------------------------------

export interface KpiItem {
  label: string;
  value: ReactNode;
  unit?: string;
  hint?: ReactNode;
  tone?: SemanticTone;
  /** Draw the status dot (defaults on when tone is set). */
  dot?: boolean;
  loading?: boolean;
  /** "unavailable" state — value shows "—" with a muted note. */
  unavailable?: boolean;
  /** Optional route; the whole cell becomes a link. */
  to?: string;
}

// Column counts per breakpoint, plus "the last item spans the rest of its row"
// rules: an odd count would otherwise leave an empty cell that reads as a
// lighter tile (the grid's hairline background shows through). The nth-child
// arithmetic picks the remainder at that breakpoint's column count; the
// `col-span-1` entries reset the narrower breakpoint's rule.
const SPAN_2 = "[&>*:last-child:nth-child(2n+1)]:col-span-2";
const SPAN_SM3 = "sm:[&>*:last-child:nth-child(2n+1)]:col-span-1 sm:[&>*:last-child:nth-child(3n+1)]:col-span-3 sm:[&>*:last-child:nth-child(3n+2)]:col-span-2";
const RESET_LG = "lg:[&>*:last-child:nth-child(2n+1)]:col-span-1 lg:[&>*:last-child:nth-child(3n+1)]:col-span-1 lg:[&>*:last-child:nth-child(3n+2)]:col-span-1";
const KPI_COLS: Record<3 | 4 | 5 | 6, string> = {
  3: `sm:grid-cols-3 ${SPAN_2} ${SPAN_SM3}`,
  4: `sm:grid-cols-2 lg:grid-cols-4 ${SPAN_2} ${RESET_LG} lg:[&>*:last-child:nth-child(4n+1)]:col-span-4 lg:[&>*:last-child:nth-child(4n+2)]:col-span-3 lg:[&>*:last-child:nth-child(4n+3)]:col-span-2`,
  5: `sm:grid-cols-3 lg:grid-cols-5 ${SPAN_2} ${SPAN_SM3} ${RESET_LG} lg:[&>*:last-child:nth-child(5n+1)]:col-span-5 lg:[&>*:last-child:nth-child(5n+2)]:col-span-4 lg:[&>*:last-child:nth-child(5n+3)]:col-span-3 lg:[&>*:last-child:nth-child(5n+4)]:col-span-2`,
  6: `sm:grid-cols-3 lg:grid-cols-6 ${SPAN_2} ${SPAN_SM3} ${RESET_LG} lg:[&>*:last-child:nth-child(6n+1)]:col-span-6 lg:[&>*:last-child:nth-child(6n+2)]:col-span-5 lg:[&>*:last-child:nth-child(6n+3)]:col-span-4 lg:[&>*:last-child:nth-child(6n+4)]:col-span-3 lg:[&>*:last-child:nth-child(6n+5)]:col-span-2`,
};

export function KpiStrip({ items, columns, className }: { items: KpiItem[]; columns?: 3 | 4 | 5 | 6; className?: string }) {
  const cols = columns ?? (Math.min(6, Math.max(3, items.length)) as 3 | 4 | 5 | 6);
  return (
    <div
      className={`grid grid-cols-2 gap-px overflow-hidden rounded-card border border-line bg-line ${KPI_COLS[cols]} ${className ?? ""}`}
      role="list"
    >
      {items.map((k) => {
        const body = (
          <>
            <div className="flex items-center gap-1.5">
              {(k.dot ?? Boolean(k.tone)) && <StatusDot tone={k.tone ?? "neutral"} size="sm" />}
              <span className="pb-micro line-clamp-2 text-text-3 sm:line-clamp-1">{k.label}</span>
            </div>
            {k.loading ? (
              <Skeleton className="mt-2.5" width="55%" height={20} />
            ) : (
              <div className="mt-1.5 min-w-0 truncate font-mono text-lg font-medium tabular-nums leading-7 tracking-[-0.01em] text-text-1">
                {k.unavailable ? "—" : k.value}
                {k.unit && !k.unavailable && <span className="ml-1 font-sans text-xs font-normal text-text-3">{k.unit}</span>}
              </div>
            )}
            {/* Captions wrap to two lines on phones; one ellipsised line from `sm`. */}
            <div className="mt-0.5 line-clamp-2 text-xs leading-4 text-text-3 sm:line-clamp-1">
              {k.unavailable ? "unavailable" : k.hint}
            </div>
          </>
        );
        const cls = "block min-w-0 bg-surface-1 px-4 py-3";
        return k.to ? (
          <Link key={k.label} to={k.to} role="listitem" className={`${cls} transition-colors duration-fast hover:bg-surface-2`}>
            {body}
          </Link>
        ) : (
          <div key={k.label} role="listitem" className={cls}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pill — status chip. Legacy tones (zinc/green/amber/red/blue/violet) still
// work; prefer the semantic ones.
// ---------------------------------------------------------------------------

type Tone = "zinc" | "green" | "amber" | "red" | "blue" | "violet" | SemanticTone;

const TONE_CLASS: Record<Tone, string> = {
  zinc: "bg-white/[0.06] text-text-2 ring-1 ring-inset ring-line",
  neutral: "bg-white/[0.06] text-text-2 ring-1 ring-inset ring-line",
  green: "bg-ok/10 text-ok ring-1 ring-inset ring-ok/25",
  ok: "bg-ok/10 text-ok ring-1 ring-inset ring-ok/25",
  amber: "bg-warn/10 text-warn ring-1 ring-inset ring-warn/30",
  warn: "bg-warn/10 text-warn ring-1 ring-inset ring-warn/30",
  red: "bg-danger/10 text-danger ring-1 ring-inset ring-danger/30",
  danger: "bg-danger/10 text-danger ring-1 ring-inset ring-danger/30",
  blue: "bg-info/10 text-info ring-1 ring-inset ring-info/30",
  info: "bg-info/10 text-info ring-1 ring-inset ring-info/30",
  violet: "bg-product-data/10 text-product-data ring-1 ring-inset ring-product-data/30",
};

export function Pill({ children, tone = "zinc", dot, className }: { children: ReactNode; tone?: Tone; dot?: boolean; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-medium leading-4 ${TONE_CLASS[tone]} ${className ?? ""}`}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// FreshnessTag — the shared "how old / is it stale" age chip. Renders a tone
// dot + the relative-age label from dataFreshnessPill(), so every data widget
// flags staleness identically. Pass the verdict object straight through.
// ---------------------------------------------------------------------------

const FRESH_TONE: Record<"green" | "amber" | "red", SemanticTone> = { green: "ok", amber: "warn", red: "danger" };
const FRESH_TEXT: Record<"green" | "amber" | "red", string> = {
  green: "text-text-3",
  amber: "text-warn",
  red: "text-danger",
};

export function FreshnessTag({
  pill,
  prefix,
  className,
}: {
  pill: { tone: "green" | "amber" | "red"; label: string; isStale: boolean };
  /** Optional lead-in, e.g. "updated" → "updated 3 min ago". */
  prefix?: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] leading-4 ${FRESH_TEXT[pill.tone]} ${pill.isStale ? "font-medium" : ""} ${className ?? ""}`}
      title={pill.isStale ? "Stale — no fresh data within the expected cadence" : undefined}
    >
      <StatusDot tone={FRESH_TONE[pill.tone]} size="sm" />
      {prefix ? `${prefix} ${pill.label}` : pill.label}
    </span>
  );
}

export function RoadmapNotice({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-control border border-warn/25 bg-warn/[0.06] p-3 text-xs leading-5 text-warn/90">
      <span className="pb-micro mt-px shrink-0 rounded bg-warn/10 px-1.5 py-0.5 text-warn">roadmap</span>
      <span>{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SegmentedControl — one neutral-active pill/tab group for every toggle
// ---------------------------------------------------------------------------

export interface SegItem<T extends string> {
  id: T;
  label: string;
  icon?: LucideIcon;
}

export function SegmentedControl<T extends string>({
  items,
  active,
  onChange,
  size = "md",
  role = "tablist",
  ariaLabel,
}: {
  items: ReadonlyArray<SegItem<T>>;
  active: T;
  onChange: (id: T) => void;
  size?: "sm" | "md";
  role?: "tablist" | "group";
  ariaLabel: string;
}) {
  // 28 / 32 px with a fine pointer; 44 px (and ≥ 44 wide) under a coarse one.
  const pad = size === "sm" ? "h-7 px-2 text-xs coarse:h-11 coarse:min-w-11 coarse:px-3 coarse:text-[13px]" : "h-8 px-3 text-[13px] coarse:h-11 coarse:min-w-11";
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  // The row scrolls sideways on phones; the edge fade appears only when an
  // option is actually hidden (useHScrollFade keeps data-overflow current).
  const rowRef = useRef<HTMLDivElement>(null);
  useHScrollFade(rowRef);
  // One tab stop for the whole control; ← → ↑ ↓ Home End move between options
  // and select them (automatic activation, like native tabs).
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const keys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"];
      if (!keys.includes(e.key) || items.length === 0) return;
      e.preventDefault();
      const current = Math.max(0, items.findIndex((it) => it.id === active));
      const next =
        e.key === "Home"
          ? 0
          : e.key === "End"
            ? items.length - 1
            : e.key === "ArrowRight" || e.key === "ArrowDown"
              ? (current + 1) % items.length
              : (current - 1 + items.length) % items.length;
      const target = items[next];
      if (!target) return;
      onChange(target.id);
      buttons.current[next]?.focus();
    },
    [items, active, onChange],
  );
  const activeIndex = items.findIndex((it) => it.id === active);
  return (
    <div
      ref={rowRef}
      role={role}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className="pb-hscroll inline-flex max-w-full items-center gap-0.5 rounded-control border border-line bg-surface-1 p-0.5 sm:flex-wrap sm:overflow-visible"
    >
      {items.map((it, i) => {
        const isActive = it.id === active;
        const Icon = it.icon;
        return (
          <button
            key={it.id}
            ref={(el) => {
              buttons.current[i] = el;
            }}
            type="button"
            role={role === "tablist" ? "tab" : undefined}
            aria-selected={role === "tablist" ? isActive : undefined}
            aria-pressed={role === "group" ? isActive : undefined}
            tabIndex={isActive || (activeIndex === -1 && i === 0) ? 0 : -1}
            onClick={() => onChange(it.id)}
            className={[
              "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[6px] font-medium transition-colors duration-fast",
              pad,
              isActive
                ? "bg-white/[0.08] text-text-1 shadow-[inset_0_0_0_1px_var(--pb-line-strong)]"
                : "text-text-2 hover:bg-white/[0.04] hover:text-text-1",
            ].join(" ")}
          >
            {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionButton — solid (the ONE lime primary per view) / outline / ghost /
// danger. `tone="red"` is kept as an alias of variant="danger".
// ---------------------------------------------------------------------------

export function ActionButton({
  variant = "solid",
  size = "md",
  icon: Icon,
  loading,
  disabled,
  onClick,
  tone = "lime",
  type = "button",
  className,
  title,
  ariaLabel,
  children,
}: {
  variant?: "solid" | "outline" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  icon?: LucideIcon;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  tone?: "lime" | "red";
  type?: "button" | "submit";
  className?: string;
  title?: string;
  ariaLabel?: string;
  children?: ReactNode;
}) {
  const v = variant === "solid" && tone === "red" ? "danger" : variant;
  // 32 / 36 / 44 px with a fine pointer; every size is 44 px (and ≥ 44 wide,
  // for icon-only buttons) under a coarse pointer at any viewport width.
  const pad =
    size === "sm"
      ? "h-8 px-2.5 text-xs coarse:h-11 coarse:min-w-11 coarse:px-3 coarse:text-[13px]"
      : size === "lg"
        ? "h-11 min-w-11 px-4 text-[15px]"
        : "h-9 px-3.5 text-sm coarse:h-11 coarse:min-w-11 coarse:px-4";
  const look: Record<string, [rest: string, hover: string]> = {
    solid: ["bg-brand text-black", "hover:bg-brand-hover"],
    outline: ["border border-line-strong bg-transparent text-text-1", "hover:border-white/25 hover:bg-white/[0.04]"],
    ghost: ["text-text-2", "hover:bg-white/[0.06] hover:text-text-1"],
    danger: ["border border-danger/40 bg-danger/10 text-danger", "hover:bg-danger/20"],
  };
  // Disabled buttons stay focusable (aria-disabled, no native `disabled`) so a
  // title / Tooltip explaining why is reachable by keyboard; clicks — including
  // the synthetic click of a form's implicit submission — are suppressed.
  // A disabled primary drops the lime for the neutral resting style.
  const isDisabled = Boolean(disabled || loading);
  const neutral = v === "solid" && Boolean(disabled) && !loading;
  const [rest, hover] = look[v] ?? look.solid!;
  const state = neutral
    ? "cursor-not-allowed border border-line bg-surface-2 text-text-3"
    : isDisabled
      ? "cursor-not-allowed opacity-50"
      : "";
  return (
    <button
      type={type}
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick?.();
      }}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      title={title}
      aria-label={ariaLabel}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-control font-semibold transition-colors duration-fast",
        pad,
        neutral ? "" : rest,
        isDisabled ? "" : hover,
        state,
        className ?? "",
      ].join(" ")}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// StatTileInteractive — KPI tile with delta pill + sparkline + explain hook
// ---------------------------------------------------------------------------

export function StatTileInteractive({
  label,
  value,
  format,
  accent = "var(--pb-text-1)",
  delta,
  spark,
  sub,
  onExplain,
  animate = true,
}: {
  label: string;
  value: number;
  format?: (n: number) => string;
  accent?: string;
  delta?: { value: number; suffix?: string; dir: "up" | "down" };
  spark?: { data: ReadonlyArray<Record<string, unknown>>; dataKey: string; color?: string };
  sub?: string;
  onExplain?: () => void;
  /** Count-up on mount. Turn off for constants/ratios where a 0→value tween is
      meaningless (e.g. a fixed emission factor). */
  animate?: boolean;
}) {
  const body = (
    <div className="flex h-full flex-col rounded-card border border-line bg-surface-1 p-4 text-left transition-colors duration-fast hover:border-line-strong">
      <div className="flex items-center justify-between gap-2">
        <span className="pb-micro text-text-3">{label}</span>
        {delta && (
          <span
            className={`inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[11px] font-medium tabular-nums ${
              delta.dir === "up" ? "bg-ok/10 text-ok" : "bg-danger/10 text-danger"
            }`}
          >
            {delta.dir === "up" ? "▲" : "▼"} {Math.abs(delta.value).toLocaleString("en-IN")}
            {delta.suffix ?? ""}
          </span>
        )}
      </div>
      <div className="mt-2 min-w-0 font-mono text-xl font-medium tabular-nums tracking-[-0.01em] [overflow-wrap:anywhere] sm:text-2xl" style={{ color: accent }}>
        {animate ? <AnimatedNumber value={value} format={format} /> : format ? format(value) : Math.round(value).toLocaleString("en-IN")}
      </div>
      {sub && <div className="mt-0.5 text-xs leading-4 text-text-3">{sub}</div>}
      {spark && (
        <div className="mt-auto pt-2">
          <Sparkline data={spark.data} dataKey={spark.dataKey} color={spark.color} height={36} />
        </div>
      )}
    </div>
  );

  if (!onExplain) return body;
  return (
    <HoverLift className="h-full">
      <button type="button" onClick={onExplain} aria-label={`Explain ${label}`} className="block h-full w-full rounded-card">
        {body}
      </button>
    </HoverLift>
  );
}

// ---------------------------------------------------------------------------
// Panel states — one consistent error / empty / loading / source set
// ---------------------------------------------------------------------------

export function ErrorPanel({ message, title, onRetry }: { message: string; title?: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex items-start gap-3 rounded-card border border-danger/30 bg-danger/[0.06] p-3 text-sm text-danger">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && <div className="font-medium">{title}</div>}
        <div className={title ? "mt-0.5 text-[13px] text-danger/80" : ""}>{message}</div>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-danger/30 px-2 text-xs font-medium text-danger hover:bg-danger/10 coarse:-my-2 coarse:h-11 coarse:min-w-11 coarse:px-3"
        >
          <RefreshCw className="h-3 w-3" aria-hidden="true" /> Retry
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  label,
  icon: Icon = Inbox,
  description,
  action,
  className,
}: {
  label: string;
  icon?: LucideIcon;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col items-center justify-center gap-2 px-4 py-10 text-center ${className ?? ""}`}>
      <span className="flex h-10 w-10 items-center justify-center rounded-card border border-line bg-surface-2 text-text-3">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="text-sm text-text-2">{label}</div>
      {description && <p className="max-w-sm text-[13px] leading-5 text-text-3">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ChartSkeleton({ height = 200 }: { height?: number }) {
  return <div className="pb-skeleton rounded-card" style={{ height: `calc(${height}px * var(--pb-chart-scale))` }} aria-hidden="true" />;
}

export function SourceUnavailablePill() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-white/[0.06] px-1.5 py-0.5 text-[11px] text-text-3">
      <WifiOff className="h-3 w-3" aria-hidden="true" /> source unavailable
    </span>
  );
}

// ---------------------------------------------------------------------------
// Toggle — switch row (label + description + switch)
// ---------------------------------------------------------------------------

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-start justify-between gap-4 py-2 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
      <span>
        <span className="block text-sm text-text-1">{label}</span>
        {description && <span className="mt-0.5 block text-xs leading-4 text-text-3">{description}</span>}
      </span>
      {/* On = off-white track with a surface-0 knob (never brand lime — the
          lime budget is for primary actions). The 20 px switch sits inside a
          44 px hit area under a coarse pointer; the focus ring stays lime. */}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className="mt-0.5 flex h-5 w-9 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 coarse:-mb-3 coarse:-mt-3 coarse:h-11 coarse:w-12 coarse:rounded-control"
      >
        <span
          className={[
            "relative block h-5 w-9 rounded-full transition-colors duration-base",
            checked ? "bg-text-1" : "bg-white/[0.14]",
          ].join(" ")}
          aria-hidden="true"
        >
          <span
            className={[
              "absolute left-0.5 top-0.5 h-4 w-4 rounded-full transition-transform duration-base",
              checked ? "translate-x-4 bg-surface-0" : "translate-x-0 bg-text-1",
            ].join(" ")}
          />
        </span>
      </button>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Toolbar — filters row: wraps on desktop, swipes on phones
// ---------------------------------------------------------------------------

export function Toolbar({ children, trailing, className, label }: { children: ReactNode; trailing?: ReactNode; className?: string; label?: string }) {
  // Phones: the children row swipes behind an edge fade that only shows while
  // a control is hidden off-screen (useHScrollFade).
  const rowRef = useRef<HTMLDivElement>(null);
  useHScrollFade(rowRef);
  return (
    <div role={label ? "toolbar" : undefined} aria-label={label} className={`flex items-center gap-2 ${className ?? ""}`}>
      {/* Below `sm` every control keeps its intrinsic width and the row scrolls;
          from `sm` the row wraps (a squeezed 26 px date trigger was the old failure). */}
      <div ref={rowRef} className="pb-hscroll flex min-w-0 flex-1 items-center gap-2 max-sm:[&>*]:shrink-0 sm:flex-wrap sm:overflow-visible">
        {children}
      </div>
      {trailing && <div className="flex shrink-0 items-center gap-2">{trailing}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SwipeArea — wrap a tab panel so a horizontal swipe (touch, 48 px) moves to
// the previous / next tab. Gestures that start on a chart or inside a
// horizontally scrolling row are left alone (useSwipe). Vertical scrolling
// stays native (`touch-action: pan-y`).
// ---------------------------------------------------------------------------

export function SwipeArea({
  onPrev,
  onNext,
  className,
  children,
  threshold,
  disabled,
}: {
  onPrev?: () => void;
  onNext?: () => void;
  className?: string;
  children: ReactNode;
  threshold?: number;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useSwipe(ref, { onLeft: onNext, onRight: onPrev, threshold, disabled });
  return (
    <div ref={ref} className={`pb-swipe min-w-0 ${className ?? ""}`}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PhoneCard — the card a DataTable row becomes below `sm`: title / subtitle,
// a status slot top-right, a two-column fact list (micro labels, numbers in
// the numeric face) and an actions row with the primary action visible.
// Pages pass `phoneCard={(row) => <PhoneCard …/>}` to DataTable, or let
// `phoneCard="auto"` derive the card from the columns.
// ---------------------------------------------------------------------------

export interface PhoneCardFact {
  label: ReactNode;
  value: ReactNode;
  /** Numeric: the numeric face with tabular figures. */
  mono?: boolean;
  /** Span both columns (long text). */
  wide?: boolean;
}

// Table cells are often `whitespace-nowrap`; inside a card they must wrap.
const WRAP = "[overflow-wrap:anywhere] [&_.whitespace-nowrap]:whitespace-normal [&_.truncate]:whitespace-normal";

export function PhoneCard({
  title,
  subtitle,
  status,
  facts,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Pill / dot shown top-right. */
  status?: ReactNode;
  facts?: ReadonlyArray<PhoneCardFact>;
  /** Buttons; the primary one should be visible without opening anything. */
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`min-w-0 ${className ?? ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className={`min-w-0 text-sm font-medium leading-5 text-text-1 ${WRAP}`}>{title}</div>
          {subtitle != null && subtitle !== "" && <div className={`mt-0.5 text-xs leading-4 text-text-3 ${WRAP}`}>{subtitle}</div>}
        </div>
        {status && <div className="shrink-0">{status}</div>}
      </div>
      {facts && facts.length > 0 && (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
          {facts.map((f, i) => (
            <div key={i} className={`min-w-0 ${f.wide ? "col-span-2" : ""}`}>
              <dt className="pb-micro truncate text-text-3">{f.label}</dt>
              <dd className={`mt-0.5 min-w-0 text-[13px] leading-5 text-text-1 ${WRAP} ${f.mono ? "font-mono tabular-nums" : ""}`}>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {actions && <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3 [&>*]:min-w-0">{actions}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataTable — sticky header, no zebra, hover row, right-aligned mono numerics,
// density from the global preference (or forced per table). Below `sm` a
// table with `phoneCard` renders one card per row instead (same loading /
// empty / error / footer states), so no phone ever scrolls a table sideways.
// ---------------------------------------------------------------------------

export interface DataColumn<T> {
  key: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  /** Numeric column: mono + tabular + right-aligned. */
  mono?: boolean;
  width?: number | string;
  className?: string;
  render: (row: T, index: number) => ReactNode;
}

const DENSITY_PAD: Record<Density, string> = { comfortable: "px-4", compact: "px-3" };

/** `phoneCard="auto"` (or this object) builds a PhoneCard from the columns. */
export interface PhoneCardAuto {
  /** Column key for the card title (default: the first column). */
  title?: string;
  /** Column key shown under the title. */
  subtitle?: string;
  /** Column key whose cell is the status slot (top-right). */
  status?: string;
  /** Column keys to leave out. */
  hide?: string[];
  /** Column keys to span the full card width. */
  wide?: string[];
  /** Column keys rendered in the actions row even though they have a text header. */
  actions?: string[];
  /** Label of the full-width button that fires onRowClick (default "Open"). */
  actionLabel?: string;
}

export type PhoneCardRenderer<T> = ((row: T, index: number) => ReactNode) | "auto" | PhoneCardAuto;

const INTERACTIVE = "a, button, input, select, textarea, [role=button], [role=switch], [role=checkbox], [role=menuitem]";

function AutoCard<T>({ row, index, columns, cfg, onRowClick }: { row: T; index: number; columns: ReadonlyArray<DataColumn<T>>; cfg: PhoneCardAuto; onRowClick?: (row: T) => void }) {
  const byKey = (k?: string) => (k ? columns.find((c) => c.key === k) : undefined);
  const titleCol = byKey(cfg.title) ?? columns[0];
  const subCol = byKey(cfg.subtitle);
  const statusCol = byKey(cfg.status);
  const taken = new Set<string>([titleCol?.key ?? "", subCol?.key ?? "", statusCol?.key ?? "", ...(cfg.hide ?? [])]);
  const wide = new Set(cfg.wide ?? []);
  const rest = columns.filter((c) => !taken.has(c.key));
  // A column without a text header ("" or an sr-only node) is an action column.
  const actionKeys = new Set(cfg.actions ?? []);
  const isAction = (c: DataColumn<T>) => actionKeys.has(c.key) || typeof c.header !== "string" || c.header.trim() === "";
  const facts: PhoneCardFact[] = rest.filter((c) => !isAction(c)).map((c) => ({ label: c.header, value: c.render(row, index), mono: c.mono, wide: wide.has(c.key) }));
  const actionCols = rest.filter(isAction);
  const actions =
    actionCols.length > 0 || onRowClick ? (
      <>
        {actionCols.map((c) => (
          <span key={c.key} className="inline-flex items-center">
            {c.render(row, index)}
          </span>
        ))}
        {onRowClick && (
          <ActionButton variant="outline" size="md" className="flex-1" onClick={() => onRowClick(row)}>
            {cfg.actionLabel ?? "Open"}
          </ActionButton>
        )}
      </>
    ) : undefined;
  return (
    <PhoneCard
      title={titleCol ? titleCol.render(row, index) : null}
      subtitle={subCol ? subCol.render(row, index) : undefined}
      status={statusCol ? statusCol.render(row, index) : undefined}
      facts={facts}
      actions={actions}
    />
  );
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  label,
  onRowClick,
  density: forced,
  stickyHeader = true,
  maxHeight,
  loading,
  error,
  emptyLabel = "Nothing to show yet.",
  emptyIcon,
  emptyAction,
  rowClassName,
  className,
  footer,
  phoneCard,
}: {
  columns: ReadonlyArray<DataColumn<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T, index: number) => string | number;
  /** Accessible table name. */
  label: string;
  onRowClick?: (row: T) => void;
  density?: Density;
  stickyHeader?: boolean;
  /** Scroll inside the box past this height (keeps the sticky header useful). */
  maxHeight?: number | string;
  loading?: boolean;
  error?: string | null;
  emptyLabel?: string;
  emptyIcon?: LucideIcon;
  emptyAction?: ReactNode;
  rowClassName?: (row: T) => string | undefined;
  className?: string;
  footer?: ReactNode;
  /**
   * Below `sm`, render one card per row instead of the table: a renderer
   * `(row, i) => <PhoneCard …/>`, `"auto"`, or an auto config naming the
   * title / subtitle / status columns. Omit to keep the table at every width.
   */
  phoneCard?: PhoneCardRenderer<T>;
}) {
  const { density: global } = useDensity();
  const density = forced ?? global;
  const pad = DENSITY_PAD[density];
  const rowStyle: CSSProperties | undefined = forced ? { height: forced === "compact" ? 36 : 44 } : { height: "var(--pb-row-h)" };
  const textSize = density === "compact" ? "text-[13px]" : "text-sm";
  const phoneWidth = useIsPhoneWidth();
  // A table wider than its box scrolls sideways behind an edge fade (the
  // scrollbar stays, so fine pointers keep their cue too). Re-armed when the
  // phone card mode hands back to the table.
  const scrollRef = useRef<HTMLDivElement>(null);
  useHScrollFade(scrollRef, phoneWidth);

  if (phoneCard && phoneWidth) {
    const cfg: PhoneCardAuto | null = phoneCard === "auto" ? {} : typeof phoneCard === "function" ? null : phoneCard;
    const custom = typeof phoneCard === "function" ? phoneCard : null;
    // A custom card with onRowClick: the whole card is the row's activator
    // (clicks on buttons / links inside it are left to them).
    const cardClickable = Boolean(custom && onRowClick);
    const activate = (row: T) => (e: MouseEvent<HTMLLIElement> | KeyboardEvent<HTMLLIElement>) => {
      if ((e.target as HTMLElement).closest(INTERACTIVE)) return;
      if ("key" in e) {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
      }
      onRowClick?.(row);
    };
    return (
      <div className={`min-w-0 ${className ?? ""}`} aria-busy={loading || undefined}>
        {loading && rows.length === 0 && (
          <ul className="space-y-2" aria-label={`${label} — loading`}>
            {Array.from({ length: 3 }, (_, i) => (
              <li key={`sk-${i}`} className="space-y-3 rounded-card border border-line bg-surface-1 p-4">
                <Skeleton width="60%" height={16} />
                <Skeleton width="40%" height={12} />
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton width="70%" height={12} />
                  <Skeleton width="55%" height={12} />
                </div>
              </li>
            ))}
          </ul>
        )}
        {!loading && rows.length === 0 && !error && (
          <div className="rounded-card border border-line bg-surface-1">
            <EmptyState label={emptyLabel} icon={emptyIcon} action={emptyAction} />
          </div>
        )}
        {rows.length > 0 && (
          <ul className="space-y-2" aria-label={label}>
            {rows.map((row, i) => (
              <li
                key={rowKey(row, i)}
                role={cardClickable ? "button" : undefined}
                tabIndex={cardClickable ? 0 : undefined}
                onClick={cardClickable ? activate(row) : undefined}
                onKeyDown={cardClickable ? activate(row) : undefined}
                className={`rounded-card border border-line bg-surface-1 p-4 ${cardClickable ? "cursor-pointer transition-colors duration-fast hover:border-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60" : ""} ${rowClassName?.(row) ?? ""}`}
              >
                {custom ? custom(row, i) : <AutoCard row={row} index={i} columns={columns} cfg={cfg ?? {}} onRowClick={onRowClick} />}
              </li>
            ))}
          </ul>
        )}
        {error && (
          <div className="mt-2">
            <ErrorPanel message={error} />
          </div>
        )}
        {footer && <div className="mt-2 px-1 text-xs text-text-3">{footer}</div>}
      </div>
    );
  }

  return (
    <div className={`overflow-hidden rounded-card border border-line bg-surface-1 ${className ?? ""}`}>
      <div ref={scrollRef} className="overflow-auto overscroll-x-contain" style={maxHeight ? { maxHeight } : undefined}>
        <table className={`w-full ${textSize}`} aria-label={label} aria-busy={loading || undefined}>
          <thead className={stickyHeader ? "sticky top-0 z-10 bg-surface-1" : undefined}>
            <tr className="border-b border-line">
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  className={`pb-micro h-9 whitespace-nowrap ${pad} font-medium text-text-3 ${
                    c.align === "right" || c.mono ? "text-right" : c.align === "center" ? "text-center" : "text-left"
                  } ${c.className ?? ""}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {loading && rows.length === 0 &&
              Array.from({ length: 4 }, (_, i) => (
                <tr key={`sk-${i}`} style={rowStyle}>
                  {columns.map((c) => (
                    <td key={c.key} className={pad}>
                      <Skeleton width={c.mono ? "45%" : "70%"} className={c.mono ? "ml-auto" : ""} />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading && rows.length === 0 && !error && (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState label={emptyLabel} icon={emptyIcon} action={emptyAction} />
                </td>
              </tr>
            )}
            {rows.map((row, i) => {
              const clickable = Boolean(onRowClick);
              return (
                <tr
                  key={rowKey(row, i)}
                  style={rowStyle}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => onRowClick?.(row) : undefined}
                  onKeyDown={
                    clickable
                      ? (e: KeyboardEvent<HTMLTableRowElement>) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick?.(row);
                          }
                        }
                      : undefined
                  }
                  className={`text-text-1 transition-colors duration-fast ${clickable ? "cursor-pointer hover:bg-white/[0.04] focus-visible:bg-white/[0.04]" : "hover:bg-white/[0.025]"} ${rowClassName?.(row) ?? ""}`}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`${pad} align-middle ${
                        c.mono
                          ? "text-right font-mono tabular-nums"
                          : c.align === "right"
                            ? "text-right"
                            : c.align === "center"
                              ? "text-center"
                              : ""
                      } ${c.className ?? ""}`}
                    >
                      {c.render(row, i)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && (
        <div className="border-t border-line p-3">
          <ErrorPanel message={error} />
        </div>
      )}
      {footer && <div className="border-t border-line px-4 py-2 text-xs text-text-3">{footer}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drawer — right-hand slide-over panel (the Tickets / Explain pattern). Below
// `sm` it renders as a BOTTOM SHEET: rounded top, grab handle, 92svh max with
// an inner scroll, safe-area bottom padding, backdrop tap and swipe-down to
// close; focus trap and focus return are the same in both modes. Landscape
// phones (≥ sm wide) keep the side sheet, padded by the right safe-area inset.
// `pager` renders prev / next controls in the header (M2 wires it for racks).
// ---------------------------------------------------------------------------

const DRAWER_W: Record<"sm" | "md" | "lg", string> = { sm: "sm:max-w-[420px]", md: "sm:max-w-[560px]", lg: "sm:max-w-[720px]" };

export interface DrawerPager {
  onPrev?: () => void;
  onNext?: () => void;
  /** "3 of 12", "B4-C17-R6" — shown between the arrows in the numeric face. */
  label?: ReactNode;
  prevLabel?: string;
  nextLabel?: string;
}

const DRAWER_ICON_BTN =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-control text-text-3 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent coarse:h-11 coarse:w-11";

export function Drawer({
  open,
  onClose,
  title,
  description,
  width = "md",
  accent,
  children,
  footer,
  pager,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  width?: "sm" | "md" | "lg";
  /** Product/semantic colour drawn as the top hairline. */
  accent?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Prev / next controls in the header (walk racks, blocks, tickets). */
  pager?: DrawerPager;
}) {
  const reduce = useReducedMotion();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // Bottom sheet below `sm`; the drag handle / header starts a framer drag on
  // the sheet (touch-only via pointer type), and a 80 px pull or a quick flick
  // closes it. The body keeps its own vertical scroll.
  const sheet = useIsPhoneWidth();
  const dragControls = useDragControls();
  // The element that opened the drawer — focus goes back to it on close. It is
  // read during the render that opens the drawer (before commit): host content
  // often autoFocuses an input during commit, which would otherwise be what
  // any effect sees as the "opener".
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (open && !wasOpenRef.current && typeof document !== "undefined") {
    openerRef.current = document.activeElement as HTMLElement | null;
  }
  wasOpenRef.current = open;

  // Restore on close. The cleanup runs both when `open` flips to false and when
  // a host unmounts the drawer while open ({open && <Drawer/>}).
  useEffect(() => {
    if (!open) return;
    const opener = openerRef.current;
    const panel = panelRef.current;
    return () => {
      openerRef.current = null;
      if (opener && opener.isConnected && opener !== document.body && !panel?.contains(opener)) opener.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const focusables = () =>
      Array.from(panelRef.current?.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE) ?? []).filter((el) => !el.hasAttribute("disabled"));
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = els[0]!;
      const last = els[els.length - 1]!;
      const active = document.activeElement;
      const inside = panelRef.current?.contains(active) ?? false;
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const id = window.setTimeout(() => panelRef.current?.focus(), 20);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      window.clearTimeout(id);
    };
  }, [open, onClose]);

  // Portalled to <body>: a transformed/animated page ancestor would otherwise
  // become the containing block and offset the "fixed" drawer.
  if (typeof document === "undefined") return null;
  const closed = sheet ? { y: "100%" } : { x: "100%" };
  const rest = sheet ? { y: 0 } : { x: 0 };
  const pagerNode = pager && (
    <div className="flex shrink-0 items-center gap-0.5" role="group" aria-label="Previous / next">
      <button type="button" onClick={pager.onPrev} disabled={!pager.onPrev} aria-label={pager.prevLabel ?? "Previous"} className={DRAWER_ICON_BTN}>
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      {pager.label !== undefined && <span className="min-w-[2ch] px-0.5 text-center font-mono text-xs tabular-nums text-text-2">{pager.label}</span>}
      <button type="button" onClick={pager.onNext} disabled={!pager.onNext} aria-label={pager.nextLabel ?? "Next"} className={DRAWER_ICON_BTN}>
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={`fixed inset-0 z-drawer flex ${sheet ? "items-end" : "justify-end"}`}
          role="presentation"
          initial={{ opacity: reduce ? 1 : 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <button type="button" aria-label="Close panel" className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onClose} />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            data-sheet={sheet ? "bottom" : "side"}
            className={
              sheet
                ? "relative flex max-h-[92svh] w-full flex-col rounded-t-tile border-t border-line-strong bg-surface-3 shadow-[0_-24px_64px_rgba(0,0,0,0.55)] outline-none"
                : `relative flex h-full w-full flex-col border-l border-line-strong bg-surface-3 shadow-[0_24px_64px_rgba(0,0,0,0.55)] outline-none ${DRAWER_W[width]}`
            }
            style={sheet ? { paddingBottom: "var(--pb-safe-bottom)" } : { paddingRight: "var(--pb-safe-right)" }}
            initial={reduce ? rest : closed}
            animate={rest}
            exit={reduce ? rest : closed}
            transition={{ type: "spring", stiffness: 380, damping: 36 }}
            drag={sheet ? "y" : false}
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.7 }}
            dragSnapToOrigin
            onDragEnd={(_, info) => {
              if (sheet && (info.offset.y > 80 || info.velocity.y > 600)) onClose();
            }}
          >
            {accent && <span className={`absolute inset-x-0 top-0 h-0.5 ${sheet ? "rounded-t-tile" : ""}`} style={{ backgroundColor: accent }} aria-hidden="true" />}
            <header
              className={`flex select-none items-start justify-between gap-3 border-b border-line ${sheet ? "touch-none px-4 pb-3 pt-2" : "px-5 py-4"}`}
              onPointerDown={sheet ? (e) => dragControls.start(e) : undefined}
            >
              {sheet && <span className="pb-grab absolute left-1/2 top-2 -translate-x-1/2" aria-hidden="true" />}
              <div className={`min-w-0 flex-1 ${sheet ? "pt-3" : ""}`}>
                <h2 id={titleId} className="text-[15px] font-semibold leading-[22px] text-text-1">
                  {title}
                </h2>
                {description && <p className="mt-0.5 text-[13px] leading-5 text-text-3">{description}</p>}
              </div>
              <div className={`flex shrink-0 items-center gap-1 ${sheet ? "pt-2" : ""}`}>
                {pagerNode}
                <button type="button" onClick={onClose} aria-label="Close" className={DRAWER_ICON_BTN}>
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </header>
            <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${sheet ? "px-4 py-4" : "px-5 py-4"}`}>{children}</div>
            {footer && <footer className={`border-t border-line ${sheet ? "px-4 py-3" : "px-5 py-3"}`}>{footer}</footer>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

const DRAWER_FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

// ---------------------------------------------------------------------------
// Collapsible — a disclosure section (used by the Overview's executive summary)
// ---------------------------------------------------------------------------

export function Collapsible({
  open,
  onToggle,
  title,
  description,
  children,
  id,
}: {
  open: boolean;
  onToggle: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  id: string;
}) {
  return (
    <section className="rounded-card border border-line bg-surface-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        className="flex w-full items-center justify-between gap-3 rounded-card px-5 py-4 text-left transition-colors duration-fast hover:bg-white/[0.03]"
      >
        <span className="min-w-0">
          <span className="block text-[15px] font-semibold leading-[22px] text-text-1">{title}</span>
          {description && <span className="mt-0.5 block text-[13px] leading-5 text-text-3">{description}</span>}
        </span>
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control border border-line text-text-3 transition-transform duration-base"
          style={{ transform: open ? "rotate(180deg)" : undefined }}
          aria-hidden="true"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.5 4.5 6 8l3.5-3.5" />
          </svg>
        </span>
      </button>
      {open && (
        <div id={id} className="border-t border-line px-5 py-5">
          {children}
        </div>
      )}
    </section>
  );
}
